/**
 * RAE — service catalog constants.
 *
 * These mirror the mockup `rae-booking-step1-service.html` tiles. In production the
 * catalog will merge with `config.products` but for v3.0 demo we ship the static list
 * so the flow renders even when `/v3/config/products` returns [] or 503s.
 */

import type { RaeService, RaeBranchChoice } from './types';

export const RAE_SERVICES: ReadonlyArray<RaeService> = [
  {
    code: 'DDA',
    category: 'personal',
    title: 'Open a checking account',
    description:
      'DDA products including Free Checking, My Student, and Ultimate. Includes identity verification and funding.',
    durationMin: 45,
    icon: '$',
  },
  {
    code: 'SAV',
    category: 'personal',
    title: 'Open a savings account',
    description: 'Statement Savings, Money Market, or HSA. Includes rate review and funding.',
    durationMin: 30,
    icon: 'S',
  },
  {
    code: 'CD',
    category: 'personal',
    title: 'Open a Certificate of Deposit (CD)',
    description:
      '3-month through 60-month terms. Includes rate comparison, maturity options, and funding.',
    durationMin: 45,
    icon: '%',
  },
  {
    code: 'IRA',
    category: 'personal',
    title: 'Open an IRA',
    description:
      'Traditional, Roth, or Rollover. Includes contribution-limit review and beneficiary setup.',
    durationMin: 60,
    icon: '◆',
  },
  {
    code: 'SDB',
    category: 'personal',
    title: 'Safe Deposit Box',
    description: 'New rental or close-out. Bring your ID and (for close-out) both keys.',
    durationMin: 30,
    icon: '▦',
  },
  {
    code: 'UPDATE_INFO',
    category: 'personal',
    title: 'Update my information',
    description:
      'Name change, address update, or beneficiary changes on existing accounts.',
    durationMin: 20,
    icon: '✎',
  },
  {
    code: 'OTHER',
    category: 'personal',
    title: 'Other questions & consultations',
    description: 'General banking questions, financial planning conversation, loan inquiry.',
    durationMin: 30,
    icon: '?',
  },
];

/**
 * Empty fallback — RAE consumes `config.branches` now. The previously
 * hardcoded array (drifted names, made-up distances) was removed; if
 * the config hasn't loaded yet, consumers render the zero state.
 */
export const RAE_STATIC_BRANCHES: ReadonlyArray<RaeBranchChoice> = [];

/** Seed slot generator — 30-min intervals 8:30 AM–5:30 PM on weekdays for the next 14 days. */
export function buildWeekdaySlots(fromDate: Date, days = 14): string[] {
  const out: string[] = [];
  for (let i = 0; i < days; i += 1) {
    const d = new Date(fromDate);
    d.setDate(fromDate.getDate() + i);
    const dow = d.getDay();
    if (dow === 0 || dow === 6) continue;
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    out.push(`${y}-${m}-${day}`);
  }
  return out;
}
